import React, { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
// @ts-expect-error TODO fix type error
const DatePicker = dynamic(() => import('react-datepicker'), { ssr: true });
import Tooltip from 'components/Generic/Tooltip';
const currentDate = new Date();
import { useI18n } from 'next-localization';
import { useDispatch } from 'react-redux';
import useHelpers from 'hooks/helpers';
import 'react-datepicker/dist/react-datepicker.css';
import { fundCentreActions } from '@/store/fundCentre';

import { useSitecoreContext } from 'hooks/useSitecoreContext';

const dateValidationRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;

interface DatePickerProps {
  inline: boolean;
  showPopper?: boolean;
  toggleFiltersAside?: () => void;
}

const DatepickerFilter = ({
  inline = false,
  showPopper = undefined,
  toggleFiltersAside,
}: DatePickerProps): JSX.Element => {
  const searchParams = new URLSearchParams(window.location.search);
  const date = searchParams.get('date');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [error, setError] = useState(false);
  const { sitecoreContext } = useSitecoreContext();
  const { t } = useI18n();
  const { updateUrlParams } = useHelpers();
  const dispatch = useDispatch();

  const onChange = (selectedDate: Date | null) => {
    if (selectedDate) {
      const date = new Date(selectedDate);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
      const day = String(date.getDate()).padStart(2, '0');

      const formattedDate = `${year}-${month}-${day}`;
      setSelectedDate(selectedDate);
      dispatch(fundCentreActions.setSelectedDate(formattedDate));
      updateUrlParams([{ name: 'date', value: formattedDate }]);
      setError(false);
    } else {
      setSelectedDate(null);
      dispatch(fundCentreActions.setSelectedDate(null));
      updateUrlParams([{ name: 'date', value: '' }]);
    }
    toggleFiltersAside?.();
  };

  const format = useMemo(() => {
    // Get the date format parts based on the locale
    const formatter = new Intl.DateTimeFormat(sitecoreContext.language);

    const parts = formatter.formatToParts(new Date());

    // Map the parts to a string format (e.g., MMMM-DD-YY or DD-MMMM-YY)
    const formatParts = parts.map((part) => {
      switch (part.type) {
        case 'day':
          return 'dd';
        case 'month':
          return 'MM';
        case 'year':
          return 'yyyy';
        default:
          return '/';
      }
    });

    // Join the parts to form the final format string
    return formatParts.join('');
  }, [sitecoreContext.language]);

  const isWeekday = (date: Date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6; // 0 = Sunday, 6 = Saturday
  };

  useEffect(() => {
    if (date && dateValidationRegex.test(date)) {
      setSelectedDate(new Date(date));
      dispatch(fundCentreActions.setSelectedDate(date));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  return (
    <div className="flex-column">
      {!inline && (
        <div className="d-flex mb-1.5">
          <label htmlFor="datePicker" className="form-label d-flex">
            {t('fundsFundCentreFiltersPricesDate')}
            {t('fundsFundCentreTooltipDatePicker') &&
              t('fundsFundCentreTooltipDatePicker') !== 'fundsFundCentreTooltipDatePicker' && (
                <Tooltip
                  tooltipContent={t('fundsFundCentreTooltipDatePicker')}
                  className="d-flex align-items-center"
                />
              )}
            <span className="sr-only" aria-live="polite" role="alert">
              {error
                ? t('fundCentreFiltersDateError')
                : t('fundCentreFiltersDateUpdated').replace(
                    '{token.date}',
                    selectedDate ? selectedDate.toLocaleDateString() : ''
                  )}
            </span>
          </label>
        </div>
      )}
      <DatePicker
        selected={selectedDate ? new Date(selectedDate) : null}
        placeholderText={error ? '' : t('selectDate')}
        showIcon={!selectedDate}
        inline={inline}
        wrapperClassName={`date-picker__wrapper ${error ? 'date-picker__wrapper--error' : ''}`}
        icon={<i className="bi bi-calendar" />}
        isClearable={!!selectedDate}
        filterDate={isWeekday}
        onInputError={() => {
          setError(true);
        }}
        id="datePicker"
        calendarStartDay={1}
        calendarIconClassName="calendar-icon"
        className="date-picker"
        popperClassName={inline ? 'date-picker-popper__inline' : ''}
        dateFormat={format}
        onChange={onChange}
        maxDate={currentDate}
        open={showPopper}
      />
      {error && (
        <p className="mt-1.5 text-error-400 field-error-text order-4">
          {t('fundCentreFiltersDateError')}
        </p>
      )}
      {!inline && (
        <p id="format" className="mt-1.5 mb-1.5 hint-text">
          {t('fundCentreFiltersDateFormat')} {format.toLowerCase()}
        </p>
      )}
    </div>
  );
};

export default DatepickerFilter;
